import React, { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import {
  Wizard,
  WizardContent,
  WizardHeader,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { Container } from "@UI/Components/Container/Container";
import { VStack, HStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "@modules/ServiceProviders/Components/messages";
import i18n from "@I18n/services/i18n";
import { useToast, ToastProps } from "@UI/hooks/useToast";
import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { Icon } from "@UI/Components/Icon/Icon";
import { Xmark } from "@UI/Components/Icons/Xmark";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { ProClientBadge } from "../../Components/ProClientBadge";
import { Flex } from "@UI/Components/Flex/Flex";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { useCompanyInformation } from "@modules/ServiceProviders/hooks/useCompanyInformation";
import { Spinner } from "@UI/Components/Spinner/Spinner";
import { ICompanyInfoForm } from "@modules/ServiceProviders/types";
import { CompanyDetailsForm } from "../../Components/CompanyDetailsForm";
import { DeleteCompanyModal } from "../../Components/DeleteCompanyModal";
import {
  createCompanyInvoicingResolver,
  formatVatNumber,
} from "../../utils/form";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { ConfirmationModal } from "@Shared/Components/ConfirmationModal/ConfirmationModal";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { IAppRoute } from "@App/types";
import { IUser } from "@modules/Users/services/users";
import useI18n from "@I18n/hooks/useI18n";

export const ClientCompanyInfoRouter = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { currentUser } = useCurrentUser();
  const intl = useI18n();
  const proClient = (currentUser as IUser)?.proClient;
  const vatPrefix = currentUser?.countryId === 2 ? "FR" : "BE";

  const {
    companyInformation,
    isLoadingCompanyInformation,
    saveCompanyInformation,
    isSavingCompanyInformation,
    deleteCompanyInformation,
    isDeletingCompanyInformation,
    destroyProIntent,
    isDestroyingProIntent,
  } = useCompanyInformation();

  const { generateRoute } = useRoutes();
  const callbackUrl = useSearchParams("callback_url");
  const navigate = useNavigate();

  const hasCompanyInfo = !!companyInformation;

  const destroyIntentionDisclosure = useDisclosure();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ICompanyInfoForm>({
    defaultValues: {
      i_register_as_enterprise: proClient,
    },
    resolver: createCompanyInvoicingResolver(vatPrefix, intl),
    mode: "onChange",
  });

  const vatNumberValue = watch("vat_number");

  useEffect(() => {
    if (vatNumberValue) {
      const newVatNumber = formatVatNumber(vatNumberValue, vatPrefix);
      if (newVatNumber !== vatNumberValue) {
        setValue("vat_number", newVatNumber);
      }
    }
  }, [vatNumberValue, vatPrefix, setValue]);

  const deleteDisclosure = useDisclosure();
  const { isMobile } = useMediaQuery();
  const toast = useToast();

  useEffect(() => {
    if (companyInformation) {
      reset({
        vat_number: companyInformation.vat_number,
        company_name: companyInformation.company_name,
        address: companyInformation.address,
        postcode: String(companyInformation.postcode || ""),
        city: companyInformation.city,
        i_register_as_enterprise: true,
      });
    } else {
      reset({
        i_register_as_enterprise: !!proClient,
        vat_number: vatPrefix + " ",
        company_name: "",
        address: "",
        postcode: "",
        city: "",
      });
    }
  }, [companyInformation, reset, vatPrefix, proClient]);

  useEffect(() => {
    // Only set prefix if value is empty/undefined, to allow user to clear it if they really want, but reset it back
    // or if form just initialized
    const currentVal = watch("vat_number");
    if (vatPrefix && (!currentVal || !currentVal.trim())) {
      setValue("vat_number", vatPrefix + " ");
    }
  }, [vatPrefix, setValue, watch]);

  const showToast = (toastData: Pick<ToastProps, "status" | "description">) => {
    const { status, description } = toastData;
    const toastId = uuidv4();
    toast({
      id: toastId,
      status,
      position: isMobile ? "bottom" : "top-right",
      duration: 3000,
      isClosable: true,
      render: () => (
        <AlertBox
          status={status}
          background="white"
          childrenContainerStyle={{ justifyContent: "start" }}
          marginTop={!isMobile ? "80px" : "initial"}
        >
          <HStack justifyContent="space-between" flexGrow={1}>
            <Text m="0" ml={3} color="gray.700">
              {description}
            </Text>
            <Icon
              as={Xmark}
              color="gray.700"
              cursor="pointer"
              onClick={() => toast.close(toastId)}
            />
          </HStack>
        </AlertBox>
      ),
    });
  };

  const onSubmit = async (data: ICompanyInfoForm) => {
    try {
      await saveCompanyInformation(data);
      showToast({
        status: "success",
        description: i18n.formatMessage(messages.saveSuccess),
      });

      if (callbackUrl && typeof callbackUrl === "string") {
        const url = new URL(callbackUrl);
        if (url.origin === window.location.origin) {
          navigate(`${url.pathname}${url.search}` as IAppRoute, {
            external: true,
          });
        } else {
          window.location.href = callbackUrl;
        }
      }
    } catch (error) {
      showToast({
        status: "error",
        description: i18n.formatMessage(messages.saveError),
      });
    }
  };

  const onDeleteAccount = async () => {
    try {
      await deleteCompanyInformation();
      deleteDisclosure.onClose();
      showToast({
        status: "success",
        description: i18n.formatMessage(messages.companyInfoDeletedSuccess),
      });
    } catch (error) {
      deleteDisclosure.onClose();
      showToast({
        status: "error",
        description: i18n.formatMessage(messages.companyInfoDeletedError),
      });
    }
  };

  const onDestroyProIntent = async () => {
    try {
      await destroyProIntent();
      destroyIntentionDisclosure.onClose();
      reset({ i_register_as_enterprise: false });
      showToast({
        status: "success",
        description: i18n.formatMessage(messages.enterpriseRegistrationRemoved),
      });
    } catch (error) {
      destroyIntentionDisclosure.onClose();
      // Revert checkbox state if it failed? Or simplify logic.
      // If we are here, likely the user still sees the error.
      // But we should ensure the checkbox remains checked if failure.
      setValue("i_register_as_enterprise", true);
      showToast({
        status: "error",
        description: i18n.formatMessage(messages.saveError),
      });
    }
  };

  const onCheckboxChange = (
    checked: boolean,
    onChange: (value: boolean) => void
  ) => {
    if (!checked && proClient) {
      destroyIntentionDisclosure.onOpen();
    } else {
      onChange(checked);
    }
  };

  if (isLoadingCompanyInformation) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Wizard paddingTop={{ base: 5, md: 8 }}>
      <WizardNav>
        <BackLink
          onClick={() => {
            if (callbackUrl && typeof callbackUrl === "string") {
              const url = new URL(callbackUrl);
              if (url.origin === window.location.origin) {
                navigate(`${url.pathname}${url.search}` as IAppRoute, {
                  external: true,
                });
              } else {
                window.location.href = callbackUrl;
              }
            } else {
              window.location.href =
                document.referrer || generateRoute(["DASHBOARD"]);
            }
          }}
        />
      </WizardNav>
      <WizardHeader marginBottom={4}>
        <Container
          textAlign="start"
          px={{ base: 0 }}
          maxW={[
            "container.md",
            "container.sm",
            "container.md",
            "container.xl",
          ]}
        >
          <VStack alignItems="start" spacing={2}>
            <Heading fontSize={{ base: "3xl", lg: "5xl" }}>
              <FormattedMessage {...messages.companyInfoTitle} />
            </Heading>
            <ProClientBadge />
            <Text fontSize={{ base: "md", lg: "lg" }} color="gray.500">
              <FormattedMessage {...messages.companyInfoProvideDetails} />
            </Text>
          </VStack>
        </Container>
      </WizardHeader>
      <WizardContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Container px={0} maxW="container.sm" ml={0}>
            <CompanyDetailsForm
              register={register}
              control={control}
              errors={errors}
              hasCompanyInfo={hasCompanyInfo}
              isSaving={isSavingCompanyInformation || isSubmitting}
              isDeleting={isDeletingCompanyInformation}
              onOpenDeleteModal={deleteDisclosure.onOpen}
              onRegisterAsEnterpriseChange={onCheckboxChange}
            />
          </Container>
        </form>

        <DeleteCompanyModal
          isOpen={deleteDisclosure.isOpen}
          onClose={deleteDisclosure.onClose}
          onConfirm={onDeleteAccount}
          isLoading={isDeletingCompanyInformation}
        />

        <ConfirmationModal
          isOpen={destroyIntentionDisclosure.isOpen}
          onClose={destroyIntentionDisclosure.onClose}
          onAction={onDestroyProIntent}
          isLoading={isDestroyingProIntent}
          title={i18n.formatMessage(messages.companyInfoSignUpAsCompany)}
          message={i18n.formatMessage(messages.confirmUnregisterEnterprise)}
          actionText={i18n.formatMessage(messages.confirm)}
        />
      </WizardContent>
    </Wizard>
  );
};
